import { Avatar, Button, Input, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useLogout } from "@/modules/auth/hooks/useAuth";
import { authService } from "@/modules/auth/services/auth.service";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { LogoutDoorIcon } from "@/shared/components/icons/LogoutDoorIcon";
import { parseApiError } from "@/shared/utils/error";
import { submitPasswordSave, submitProfileSave } from "../services/profile.api";

const roleLabels = {
	admin: "Org Admin",
	manager: "Manager",
	super: "Super Admin",
} as const;

function getNameParts(fullName?: string) {
	const parts = (fullName ?? "").trim().split(/\s+/).filter(Boolean);

	return {
		firstName: parts[0] ?? "",
		lastName: parts.slice(1).join(" "),
	};
}

function getInitials(fullName?: string, username?: string) {
	const source = fullName?.trim() || username?.trim() || "?";

	return source
		.split(/\s+/)
		.map((part) => part[0] ?? "")
		.join("")
		.slice(0, 2)
		.toUpperCase();
}

export function ProfilePage() {
	const user = useAuthStore((state) => state.user);
	const setUser = useAuthStore((state) => state.setUser);
	const logoutMutation = useLogout();

	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [username, setUsername] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");

	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [repeatPassword, setRepeatPassword] = useState("");

	const nameParts = useMemo(() => getNameParts(user?.fullName), [user?.fullName]);
	const initials = useMemo(() => getInitials(user?.fullName, user?.username), [user]);
	const displayName = user?.fullName?.trim() || user?.username || "Foydalanuvchi";
	const roleLabel = user ? roleLabels[user.role] : "-";

	useEffect(() => {
		if (user) {
			setFirstName(user.firstName || nameParts.firstName);
			setLastName(user.lastName || nameParts.lastName);
			setUsername(user.username || "");
			setEmail(user.email || "");
			setPhone(user.phone || "");
		}
	}, [user, nameParts]);

	const handleProfileSave = async () => {
		if (!user?.id) {
			return;
		}
		try {
			await submitProfileSave(user.id, {
				firstName,
				lastName,
				phone,
				email: email || undefined,
				username: username || undefined,
			});
			message.success("Profil muvaffaqiyatli saqlandi");

			// Refresh user in auth store
			const updatedUser = await authService.getMe();
			setUser(updatedUser);
		} catch (error: unknown) {
			message.error(parseApiError(error) || "Profilni saqlashda xatolik yuz berdi");
		}
	};

	const handlePasswordSave = async () => {
		if (newPassword !== repeatPassword) {
			message.warning("Yangi parol takrori mos emas");
			return;
		}
		if (newPassword.length < 6) {
			message.warning("Yangi parol kamida 6 ta belgidan iborat bo'lishi kerak");
			return;
		}

		try {
			await submitPasswordSave({
				currentPassword,
				newPassword,
				confirmPassword: repeatPassword,
			});
			message.success("Parol muvaffaqiyatli o'zgartirildi");
			setCurrentPassword("");
			setNewPassword("");
			setRepeatPassword("");
		} catch (error: unknown) {
			message.error(parseApiError(error) || "Parolni o'zgartirishda xatolik yuz berdi");
		}
	};

	return (
		<div className="crm-profile-page">
			<div className="crm-page-head">
				<div>
					<h1>Profil</h1>
					<p>Shaxsiy ma'lumotlar va parol sozlamalari</p>
				</div>
			</div>

			<div className="crm-profile-grid">
				<section className="crm-card crm-profile-card">
					<div className="crm-card-head">
						<h3>Mening ma'lumotlarim</h3>
					</div>
					<div className="crm-card-body">
						<div className="crm-profile-person">
							<Avatar className="crm-mini-avatar blue">{initials}</Avatar>
							<div>
								<strong>{displayName}</strong>
								<span className="crm-badge blue">{roleLabel}</span>
							</div>
						</div>

						<label className="crm-field" htmlFor="profile-first-name">
							<span>Ism</span>
							<Input
								id="profile-first-name"
								value={firstName}
								onChange={(event) => setFirstName(event.target.value)}
							/>
						</label>
						<label className="crm-field" htmlFor="profile-last-name">
							<span>Familiya</span>
							<Input
								id="profile-last-name"
								value={lastName}
								onChange={(event) => setLastName(event.target.value)}
							/>
						</label>
						<label className="crm-field" htmlFor="profile-phone">
							<span>Telefon</span>
							<Input
								id="profile-phone"
								value={phone}
								onChange={(event) => setPhone(event.target.value)}
							/>
						</label>
						<label className="crm-field" htmlFor="profile-username">
							<span>Username (login)</span>
							<Input
								id="profile-username"
								value={username}
								onChange={(event) => setUsername(event.target.value)}
							/>
						</label>
						<label className="crm-field" htmlFor="profile-email">
							<span>Email</span>
							<Input
								id="profile-email"
								value={email}
								onChange={(event) => setEmail(event.target.value)}
							/>
						</label>

						<div className="crm-profile-meta">
							Akkaunt yaratilgan:{" "}
							{user?.createdAt ? new Date(user.createdAt).toLocaleString("uz-UZ") : "-"}
						</div>

						<div className="crm-profile-actions">
							<Button className="crm-primary-btn" onClick={handleProfileSave} type="primary">
								Saqlash
							</Button>
						</div>
					</div>
				</section>

				<section className="crm-card crm-profile-card">
					<div className="crm-card-head">
						<h3>Parolni o'zgartirish</h3>
					</div>
					<div className="crm-card-body">
						<label className="crm-field" htmlFor="profile-current-password">
							<span>Joriy parol</span>
							<Input.Password
								id="profile-current-password"
								placeholder="********"
								value={currentPassword}
								onChange={(event) => setCurrentPassword(event.target.value)}
							/>
						</label>
						<label className="crm-field" htmlFor="profile-new-password">
							<span>Yangi parol</span>
							<Input.Password
								id="profile-new-password"
								placeholder="********"
								value={newPassword}
								onChange={(event) => setNewPassword(event.target.value)}
							/>
						</label>
						<label className="crm-field" htmlFor="profile-repeat-password">
							<span>Takrorlang</span>
							<Input.Password
								id="profile-repeat-password"
								placeholder="********"
								value={repeatPassword}
								onChange={(event) => setRepeatPassword(event.target.value)}
							/>
						</label>

						<div className="crm-profile-actions">
							<Button
								className="crm-danger-btn"
								icon={<LogoutDoorIcon />}
								onClick={() => logoutMutation.mutate()}
								loading={logoutMutation.isPending}
							>
								Chiqish
							</Button>
							<Button className="crm-primary-btn" onClick={handlePasswordSave} type="primary">
								Saqlash
							</Button>
						</div>
					</div>
				</section>
			</div>
		</div>
	);
}

export default ProfilePage;
